import React, { Component, ErrorInfo, ReactNode } from 'react';
import { colors, ACTION_BTN_RADIUS } from '../theme';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '40px 24px',
        textAlign: 'center',
      }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={colors.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 style={{
          fontSize: 22,
          fontWeight: 700,
          color: colors.textPrimary,
          margin: '0 0 12px',
        }}>
          Something went wrong
        </h1>
        <p style={{
          fontSize: 15,
          color: colors.textMuted,
          margin: '0 0 32px',
          maxWidth: 380,
          lineHeight: 1.6,
        }}>
          An unexpected error occurred. Refreshing the page usually fixes this.
        </p>
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <pre style={{
            background: colors.bgPage,
            border: `1px solid ${colors.borderSoft}`,
            borderRadius: ACTION_BTN_RADIUS,
            padding: '12px 16px',
            fontSize: 12,
            color: colors.danger,
            maxWidth: 560,
            textAlign: 'left',
            overflowX: 'auto',
            marginBottom: 24,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {this.state.error.message}
          </pre>
        )}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              height: 38,
              padding: '0 20px',
              background: colors.bgSurface,
              color: colors.textSecondary,
              border: `1px solid ${colors.borderInput}`,
              borderRadius: ACTION_BTN_RADIUS,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              height: 38,
              padding: '0 20px',
              background: colors.brand,
              color: '#fff',
              border: 'none',
              borderRadius: ACTION_BTN_RADIUS,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Refresh page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
